import React, { isValidElement, cloneElement, createElement } from 'react';
import { defaultProps, withProps } from '@team-griffin/rehook';
import { withBreakpointsHook } from '@ux/responsive';
import pureEnhance from '@ux/pure-enhance';
import cssSides, { Side } from '@team-griffin/css-sides';
import longhand from '@team-griffin/css-longhand';
import { useStyles } from '@team-griffin/stylr';
import { gu2px, Curvature } from '@ux/theme-utils';
import { percentage, modifier, srOnly } from '@ux/style-utils';
import { omit, not, ifElse, propSatisfies } from 'ramda';
import Helmet from 'react-helmet';
import ReactModal from 'react-modal';
import { headingS } from '@ux/type-size';
import { Display } from '@ux/typeface';
import { createPaintbrushUrl } from '@ux/glyphs';
import SimpleMessage from '@ux/simple-message';
import { isNotNilOrEmpty } from '@team-griffin/capra';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var roundAllBorders = function roundAllBorders(radius) {
  return longhand('border', {
    topLeftRadius: radius,
    topRightRadius: radius,
    bottomLeftRadius: radius,
    bottomRightRadius: radius
  });
};

var stylesheet = (function (_ref) {
  var _useStyles;

  var isMinMd = _ref.isMinMd,
      isMinSm = _ref.isMinSm,
      theme = _ref.theme;
  var curvature = theme.curvature;
  return useStyles((_useStyles = {
    default: {
      overlay: {
        // Setting this to null override
        // the default background colour that
        // the core modal inlines. This needs
        // to be overrided so that we can set it
        // via css. Why css? For the transitions
        backgroundColor: null,
        overflowX: 'hidden',
        overflowY: 'scroll',
        zIndex: 11
      },
      content: Object.assign(Object.assign(Object.assign(Object.assign({
        position: 'relative'
      }, longhand('border', {
        radius: 0,
        width: 0
      })), {
        bottom: 'auto'
      }), cssSides('padding', Side.A, 0)), {
        top: 0,
        left: 0,
        right: 0,
        width: percentage(100),
        marginBottom: gu2px(5, theme.spacing)
      })
    }
  }, _defineProperty(_useStyles, modifier('curvature', Curvature.OFF), {
    content: Object.assign({}, roundAllBorders(0))
  }), _defineProperty(_useStyles, modifier('curvature', Curvature.WEAK), {
    content: Object.assign({}, roundAllBorders(8))
  }), _defineProperty(_useStyles, modifier('curvature', Curvature.STRONG), {
    content: Object.assign({}, roundAllBorders(20))
  }), _defineProperty(_useStyles, "isMinSm", {
    content: {
      maxWidth: 600,
      top: gu2px(5, theme.spacing),
      transform: 'translateX(-50%)',
      left: percentage(50)
    }
  }), _defineProperty(_useStyles, "isMinMd", {
    content: {
      maxWidth: 740
    }
  }), _useStyles), [{
    curvature: isMinSm && curvature
  }, {
    isMinSm: isMinSm
  }, {
    isMinMd: isMinMd
  }]);
});

// we are unable to inline everything we want to style.
// We need to be able to style the transitions, this
// can only be done via css. So this function is the
// css styles

var computeCSSStyles = function computeCSSStyles(_ref) {
  var outSpeed = _ref.outSpeed;
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("style", null, "\n          .ReactModal__Body--open {\n            overflow: hidden;\n          }\n          .ReactModal__Overlay {\n            background-color: rgba(0,0,0,0.0);\n          }\n          .ReactModal__Overlay--after-open {\n            background-color: rgba(0,0,0,0.7);\n            transition: background-color 500ms ease-in-out;\n          }\n          .ReactModal__Overlay--before-close {\n            background-color: rgba(0,0,0,0.0);\n            transition: background-color 300ms ease-in-out;\n          }\n          .ReactModal__Content {\n            opacity: 0;\n            transform: scale(0.95);\n            background-color: none;\n          }\n          .ReactModal__Content--after-open {\n            transform: scale(1);\n            opacity: 1;\n            transition:\n              opacity 300ms ease-in-out 100ms;\n          }\n          .ReactModal__Content--before-close {\n            transform: scale(0.95);\n            opacity: 0;\n            transition:\n              opacity ".concat(outSpeed, "ms ease-in-out;\n          }\n        ")));
};

var PureModal = function PureModal(_ref2) {
  var styles = _ref2.styles,
      cssStyles = _ref2.cssStyles,
      outSpeed = _ref2.outSpeed,
      contentLabel = _ref2.contentLabel,
      modalProps = _ref2.modalProps,
      children = _ref2.children;
  return /*#__PURE__*/React.createElement("div", null, cssStyles, /*#__PURE__*/React.createElement(ReactModal, _extends({
    key: "modal",
    closeTimeoutMS: outSpeed,
    contentLabel: contentLabel,
    style: {
      overlay: styles.overlay,
      content: styles.content
    }
  }, modalProps), children));
};

var enhance = pureEnhance('ModalWrapper', {
  hooks: [defaultProps({
    outSpeed: 200,
    contentLabel: 'Modal'
  }), withBreakpointsHook, withProps(function (ownerProps) {
    return {
      cssStyles: computeCSSStyles(ownerProps),
      modalProps: omit(['outSpeed', 'isMinSm', 'isMinMd', 'theme'], ownerProps)
    };
  }), withProps(stylesheet)]
});
var Modal = enhance(PureModal); // eslint-disable-next-line no-underscore-dangle

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

var stylesheet$1 = (function (_ref) {
  var dismissible = _ref.dismissible,
      theme = _ref.theme;
  var palette = theme.palette;
  return useStyles({
    default: {
      root: Object.assign(Object.assign({
        overflow: 'hidden',
        position: 'relative'
      }, longhand('padding', {
        left: gu2px(3, theme.spacing),
        right: gu2px(8, theme.spacing)
      })), {
        background: "linear-gradient(\n          to right,\n          ".concat(palette.primary, " 0%,\n          ").concat(palette.primaryGradient, " 100%\n        )\n        "),
        color: palette.contrast
      }),
      title: Object.assign(Object.assign(Object.assign({}, longhand('margin', {
        top: gu2px(2, theme.spacing),
        bottom: gu2px(2, theme.spacing),
        left: 0,
        right: 0
      })), headingS()), {
        color: palette.contrast,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      })
    },
    undismissible: {
      root: {
        paddingRight: 20
      }
    }
  }, [{
    undismissible: not(dismissible)
  }]);
});

var stylesheet$2 = (function (_ref) {
  var theme = _ref.theme;
  var palette = theme.palette;
  return useStyles({
    default: {
      close: Object.assign(Object.assign({
        outline: 0,
        position: 'absolute',
        right: gu2px(3, theme.spacing),
        top: percentage(50),
        backgroundColor: 'transparent',
        border: 0,
        cursor: 'pointer',
        marginTop: -12
      }, cssSides('padding', Side.A, 0)), {
        fill: palette.contrast,
        width: 24,
        height: 24
      }),
      //@ts-ignore
      closeText: Object.assign({}, srOnly),
      closeIcon: {
        display: 'block',
        width: 24,
        height: 24
      }
    }
  }, []);
});

var PureCloseButton = function PureCloseButton(_ref) {
  var theme = _ref.theme,
      styles = _ref.styles,
      onCloseClick = _ref.onCloseClick,
      closeA11yText = _ref.closeA11yText;
  return /*#__PURE__*/React.createElement("button", {
    style: styles.close,
    type: "button",
    onClick: onCloseClick,
    "data-test-id": "closeButton"
  }, /*#__PURE__*/React.createElement("span", {
    style: styles.closeText
  }, closeA11yText), /*#__PURE__*/React.createElement("img", {
    src: createPaintbrushUrl('cross', [theme.palette.primaryContrast]),
    alt: "cross",
    loading: "lazy"
  }));
};

var enhanceButton = pureEnhance('CloseButton', {
  hooks: [withProps(stylesheet$2)]
});
var CloseButton = enhanceButton(PureCloseButton); // eslint-disable-next-line no-underscore-dangle

var PureModalHeader = function PureModalHeader(_a) {
  var styles = _a.styles,
      theme = _a.theme,
      children = _a.children,
      dismissible = _a.dismissible,
      rest = __rest(_a, ["styles", "theme", "children", "dismissible"]);

  return /*#__PURE__*/React.createElement("div", {
    style: styles.root
  }, /*#__PURE__*/React.createElement(Display, {
    component: "h1",
    style: styles.title
  }, children), dismissible ? /*#__PURE__*/React.createElement(CloseButton, _extends({
    theme: theme
  }, rest)) : null);
};

var enhance$1 = pureEnhance('ModalHeader', {
  hooks: [defaultProps({
    closeA11yText: 'Close',
    dismissible: true
  }), withProps(stylesheet$1)]
});
var ModalHeader = enhance$1(PureModalHeader); // eslint-disable-next-line no-underscore-dangle

var PureStandardModal = function PureStandardModal(_a) {
  var Component = _a.Component,
      theme = _a.theme,
      title = _a.title,
      hasTitle = _a.hasTitle,
      titleText = _a.titleText,
      hasTitleText = _a.hasTitleText,
      handleClose = _a.actions.closeModal,
      rest = __rest(_a, ["Component", "theme", "title", "hasTitle", "titleText", "hasTitleText", "actions"]);

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ModalHeader, _extends({
    theme: theme,
    onCloseClick: handleClose
  }, rest), hasTitle ? /*#__PURE__*/React.createElement(SimpleMessage, {
    id: title
  }) : null, hasTitleText ? /*#__PURE__*/React.createElement("div", null, titleText) : null), Component);
};

var enhance$2 = pureEnhance('StandardModal', {
  hooks: [withProps(function (ownerProps) {
    return {
      Component: ifElse(propSatisfies(isValidElement, 'component'), function (_ref) {
        var component = _ref.component;
        return cloneElement(component, omit(['actions', 'component', 'titleText', 'theme'], ownerProps));
      }, function (_ref2) {
        var component = _ref2.component;
        return createElement(component, omit(['actions', 'component', 'titleText', 'theme'], ownerProps));
      })(ownerProps),
      hasTitle: isNotNilOrEmpty(ownerProps.title),
      hasTitleText: isNotNilOrEmpty(ownerProps.titleText)
    };
  })]
});
var StandardModal = enhance$2(PureStandardModal); // eslint-disable-next-line no-underscore-dangle

export default Modal;
export { Modal, ModalHeader, StandardModal };
